// PATCH employee availability
import api from 'api/axios';
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

export async function patchEmployeeAvailability(employeeId, availabilityId, schedulePatch) {
  const token = localStorage.getItem('token');
  const res = await fetch(`${API_BASE_URL}/owner/employees/${employeeId}/availability/${availabilityId}/`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Token ${token}` } : {})
    },
    body: JSON.stringify({ schedule: schedulePatch })
  });
  if (!res.ok) throw new Error('Failed to update employee availability');
  return await res.json();
}
// Fetch employees from backend
// Fetch employees from backend and map to UI shape
export const fetchEmployees = async () => {
  const token = localStorage.getItem('token');
  const res = await fetch(`${API_BASE_URL}/owner/employees/`, {
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Token ${token}` } : {})
    }
  });
  if (!res.ok) {
    const errorText = await res.text();
    console.error('Failed to fetch employees:', res.status, errorText);
    throw new Error(`Failed to fetch employees: ${res.status}`);
  }
  
  // Check if response is JSON
  const contentType = res.headers.get('content-type');
  if (!contentType || !contentType.includes('application/json')) {
    const responseText = await res.text();
    console.error('Non-JSON response received:', responseText.substring(0, 200));
    throw new Error('Received non-JSON response from API');
  }
  
  const data = await res.json();
  const employeesArr = Array.isArray(data) ? data : data.results || [];
  // Map to include fullName and substoreId for UI compatibility
  return employeesArr.map((emp) => ({
    ...emp,
    fullName: emp.fullName || emp.username || emp.email || '',
    substoreId: emp.substore?.id || 0,
    employment_type: emp.employment_type || '',
    work_hours: emp.work_hours || 0,
    address: emp.address || ''
  }));
};

// Fetch paginated, searchable, and sortable employees from backend
// params: { page, page_size, search, ordering, ...filter }
export const fetchPaginatedEmployees = async (params = {}) => {
  const token = localStorage.getItem('token');
  const query = new URLSearchParams(params).toString();
  const url = `${API_BASE_URL}/owner/employees/paginated/?${query}`;
  const res = await fetch(url, {
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Token ${token}` } : {})
    }
  });
  if (!res.ok) throw new Error('Failed to fetch paginated employees');
  const data = await res.json();
  // DRF paginated response: { count, next, previous, results }
  const employeesArr = Array.isArray(data.results) ? data.results : [];
  return {
    count: data.count,
    next: data.next,
    previous: data.previous,
    results: employeesArr.map((emp) => ({
      ...emp,
      fullName: emp.fullName || emp.username || emp.email || '',
      substoreId: emp.substore?.id || 0
    }))
  };
};

export const fetchOwnerEmployees = async () => {
  const response = await api.get('/owner/employees/');
  return response.data;
};

/**
 * Get list of employees for a substore
 *
 * @param {number|string} substoreId - ID of the substore to get employees for
 * @returns {Promise} - API response with employees data
 */
export async function getEmployees(substoreId) {
  try {
    const token = localStorage.getItem('token');
    const response = await api.get(`/employee/employees/?substore_id=${substoreId}`, {
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Token ${token}` } : {})
      }
    });
    return response.data;
  } catch (error) {
    // Fallback to owner endpoint if employee endpoint fails
    try {
      const response = await api.get('/owner/employees/', {
        params: { substore_id: substoreId }
      });
      return { employees: response.data };
    } catch (fallbackError) {
      console.error('Error fetching employees:', fallbackError);
      throw fallbackError;
    }
  }
}

// Fetch employee availability with a short delay to avoid DB lock
export async function fetchEmployeeAvailability(employeeId) {
  await new Promise((resolve) => setTimeout(resolve, 300)); // 300ms delay
  const token = localStorage.getItem('token');
  const res = await fetch(`${API_BASE_URL}/owner/employees/${employeeId}/availability/`, {
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Token ${token}` } : {})
    }
  });
  if (!res.ok) throw new Error('Failed to fetch employee availability');
  return await res.json();
}

// Create employee availability (initial setup)
export async function createEmployeeAvailability(employeeId, schedule) {
  const token = localStorage.getItem('token');
  const res = await fetch(`${API_BASE_URL}/owner/employees/${employeeId}/availability/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Token ${token}` } : {})
    },
    body: JSON.stringify({ schedule })
  });
  if (!res.ok) throw new Error('Failed to create employee availability');
  return await res.json();
}

export async function deleteEmployee(id) {
  const token = localStorage.getItem('token');
  const res = await fetch(`${API_BASE_URL}/owner/employees/${id}/`, {
    method: 'DELETE',
    headers: {
      'Authorization': 'Token ' + token
    }
  });
  if (!res.ok) throw new Error('Failed to delete employee');
}